import React from 'react'
import styled, { css } from 'styled-components'

import { fastTransition } from '../../styles/effects'

type SwitchSize = 'small' | 'medium'

interface IProps {
  isOn: boolean
  size?: SwitchSize
  disabled?: boolean
  // When provided the switch renders as an interactive button; otherwise it is a
  // presentational element (e.g. inside an already-clickable row that toggles it).
  onToggle?: () => void
  ariaLabel?: string
}

// Reusable on/off toggle switch (track + sliding knob). Used by the module
// settings rows (interactive) and the filter option rows (presentational).
const Switch: React.FC<IProps> = ({
  isOn,
  size = 'medium',
  disabled,
  onToggle,
  ariaLabel,
}) => {
  const knob = <Knob $size={size} $isOn={isOn} />
  return onToggle ? (
    <TrackButton
      type="button"
      disabled={disabled}
      onClick={onToggle}
      aria-pressed={isOn}
      aria-label={ariaLabel}
      $size={size}
      $isOn={isOn}
      $disabled={disabled}
    >
      {knob}
    </TrackButton>
  ) : (
    <TrackSpan aria-hidden $size={size} $isOn={isOn} $disabled={disabled}>
      {knob}
    </TrackSpan>
  )
}

export default Switch

// [trackWidth, trackHeight, knobSize, inset] per size, in px.
const DIMENSIONS: Record<SwitchSize, [number, number, number, number]> = {
  small: [28, 16, 12, 2],
  medium: [48, 28, 22, 3],
}

const trackStyles = css<{
  $size: SwitchSize
  $isOn: boolean
  $disabled?: boolean
}>`
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  border: none;
  padding: 0;
  width: ${({ $size }) => DIMENSIONS[$size][0]}px;
  height: ${({ $size }) => DIMENSIONS[$size][1]}px;
  border-radius: ${({ $size }) => DIMENSIONS[$size][1] / 2}px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  background-color: ${({ theme, $isOn }) =>
    $isOn ? theme.colors.primary : theme.colors.textLighter};
  transition: background-color ${fastTransition};

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
    `}
`

const TrackButton = styled.button`
  ${trackStyles}
`

const TrackSpan = styled.span`
  ${trackStyles}
`

const Knob = styled.span<{ $size: SwitchSize; $isOn: boolean }>`
  position: absolute;
  top: ${({ $size }) => DIMENSIONS[$size][3]}px;
  left: ${({ $size, $isOn }) => {
    const [width, , knob, inset] = DIMENSIONS[$size]
    return $isOn ? width - knob - inset : inset
  }}px;
  width: ${({ $size }) => DIMENSIONS[$size][2]}px;
  height: ${({ $size }) => DIMENSIONS[$size][2]}px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.lightest};
  transition: left ${fastTransition};
`
